import React, { createContext, useContext, useState, useCallback } from "react";
import { Box, IconButton, Snackbar } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";

type ToastType = "success" | "error";

interface ToastState {
  open: boolean;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    type: "success",
  });

  const showToast = useCallback((message: string, type: ToastType) => {
    setToast({ open: true, message, type });
  }, []);

  const showSuccess = useCallback((message: string) => {
    showToast(message, "success");
  }, [showToast]);

  const showError = useCallback((message: string) => {
    showToast(message, "error");
  }, [showToast]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError }}>
      {children}
      <Snackbar
        open={toast.open}
        // Errors wait to be dismissed. They carry the reason an upload was refused, and
        // four seconds is not long enough to read a rejection from the Survey API, let
        // alone copy it. A success has nothing to study, so it still goes on its own.
        autoHideDuration={toast.type === "error" ? null : 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            // Top-aligned: the icon reads as attached to the first line rather than
            // floating beside the middle of a message that ran to three.
            alignItems: "flex-start",
            padding: "15px 10px",
            gap: "5px",
            // Grows with the message instead of clipping it, up to a width that does not
            // become a banner across the screen.
            maxWidth: "420px",
            background: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {toast.type === "success" ? (
            <CheckCircleIcon
              sx={{
                width: "21px",
                height: "18px",
                color: "#0A0A0A",
                flexShrink: 0,
              }}
            />
          ) : (
            <ErrorIcon
              sx={{
                width: "21px",
                height: "18px",
                color: "#DC2626",
                flexShrink: 0,
              }}
            />
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: "Inter, sans-serif",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              letterSpacing: "-0.150391px",
              color: "#0A0A0A",
              // API errors carry ids, paths and URLs with no space to break on, which
              // would otherwise run straight out of the box.
              wordBreak: "break-word",
              // A stack trace should not take the screen: the box grows to here and the
              // rest scrolls inside it.
              maxHeight: "40vh",
              overflowY: "auto",
            }}
          >
            {toast.message}
          </Box>
          {toast.type === "error" && (
            <IconButton
              aria-label="Dismiss"
              size="small"
              onClick={() => handleClose()}
              sx={{ flexShrink: 0, marginTop: "-4px", color: "#0A0A0A" }}
            >
              <CloseIcon sx={{ width: 16, height: 16 }} />
            </IconButton>
          )}
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
